import { useId, type ReactNode } from 'react'
import { actionState, type FlowAction } from './flow'
import './flow.css'

// The footer of a step or a long form, held at the bottom of the window
// (release 3.3, task F2 of the 7 Oct plan, rules 2.1 and 2.3).
//
// # WHY THIS EXISTS
//
// A step's Next sat under its last field, so on a 720 px laptop the person
// scrolled to find it, and a form whose button was off said why nowhere near
// the button. The footer stays in view at the bottom of the window while the
// step scrolls, and holds everything about leaving the step: Back, the one
// main action, what that action will do, and when it is off, why.
//
// # THE ORDER, EVERYWHERE
//
// Back on the left, then whatever else (Save and exit, Cancel), then the main
// action last on the right: PatternFly's wizard and the console's dialog
// footer (kit/Dialog.tsx on the open dialog branch) both put it there, and a
// person who has done it once looks there again. One main action only (rule
// 2.3). A quiet note (`saved`, "Draft saved") sits beside Back.
//
// # WHEN THE MAIN ACTION IS OFF
//
// It is drawn off, never hidden, and the reason is drawn beside it in words
// and tied to it with aria-describedby (flow.ts, actionState), so a screen
// reader hears why with the button.

function Act({ a, busy, cls, describedBy }: { a: FlowAction; busy: boolean; cls: string; describedBy?: string }) {
  const s = actionState(a, busy)
  const tone = a.tone === 'destructive' ? ' danger' : a.tone === 'plain' || cls !== 'primary' ? '' : ' primary'
  if (a.href && !s.off) {
    return (
      <a className={'button' + tone} href={a.href} data-action={a.actionId} data-action-also={a.actionAlso}>
        {a.label}
      </a>
    )
  }
  return (
    <button
      type={a.form ? 'submit' : 'button'}
      form={a.form}
      className={tone.trim() || undefined}
      disabled={s.off}
      aria-describedby={s.off && describedBy ? describedBy : undefined}
      data-action={a.actionId}
      data-action-also={a.actionAlso}
      onClick={a.onClick}
    >
      {a.label}
    </button>
  )
}

export function StickyFooter({
  back,
  primary,
  more,
  note,
  saved,
  busy = false,
  label = 'Step actions',
}: {
  back?: FlowAction
  primary?: FlowAction
  // Other things to do from here (Save and exit, Cancel), between Back and the
  // main action.
  more?: FlowAction[]
  // What the main action will do, in a few words: "Will import 3 vendors,
  // skip 1."
  note?: ReactNode
  // A quiet word beside Back: "Draft saved".
  saved?: ReactNode
  busy?: boolean
  label?: string
}) {
  const id = useId()
  const main = actionState(primary, busy)
  return (
    <div className="kit-foot" role="group" aria-label={label}>
      <div className="kit-foot-left">
        {back && <Act a={back} busy={busy} cls="back" />}
        {saved && <span className="kit-foot-saved">{saved}</span>}
      </div>
      <div className="kit-foot-right">
        {note && <span className="kit-foot-note">{note}</span>}
        {more?.map((a, i) => <Act key={a.label + ':' + i} a={a} busy={busy} cls="more" />)}
        {primary && main.why && (
          <span className="kit-foot-why" id={id + 'why'}>
            {main.why}
          </span>
        )}
        {/* Tied to the reason only while a reason is drawn: while busy there
            is none, and an id pointing at nothing is read as nothing. */}
        {primary && <Act a={primary} busy={busy} cls="primary" describedBy={main.why ? id + 'why' : undefined} />}
      </div>
    </div>
  )
}
